import { useQuery } from "@tanstack/react-query";
import { isAuthenticated } from "Utils/KeycloakWrapper";
import { hasProperty } from "Utils/generalUtils";
import { ErrorContext, Log } from "Utils/sentryLogUtils";
import { LicensePlateValidationType } from "Validation/index";
import { ValidationType } from "Validation/licensePlateValidation";
import {
  DetailedVehicle,
  fetchCurrentUsersVehicles,
  fetchVehicle,
  Vehicle,
  WarrantyStatus,
} from ".";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const useUsersVehicles = () =>
  useQuery({
    queryKey: ["usersVehicles"],
    queryFn: async () => {
      try {
        const vehicles = await fetchCurrentUsersVehicles();
        return processVehicles(vehicles);
      } catch (error) {
        const errorContext: ErrorContext = {
          tags: {
            message: `Unable to retrieve vehicles associated with authenticated user due to [error=${error}]`,
          },
          extras: { error },
        };
        Log.error("registeredVehicles-on-user", errorContext);
        throw error;
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: isAuthenticated(),
  });

async function processVehicles(
  vehicles: Array<Vehicle>,
): Promise<Array<DetailedVehicle | Vehicle>> {
  const licensePlateRegex = new RegExp(
    LicensePlateValidationType.DEFAULT_LICENSE_PLATE,
  );

  const processedVehicles = vehicles.map(
    (vehicle): Promise<DetailedVehicle | Vehicle> => {
      return new Promise((resolve) => {
        const isCar = licensePlateRegex.test(vehicle.regNumber);

        if (isCar && vehicle.warrantyStatus === WarrantyStatus.ACTIVE) {
          // TODO: we want to always resolve the promise for now, until we know more about
          // why the error message related to the process is shown as often as it does
          return fetchVehicle(vehicle.regNumber.replace(/\s/g, ""))
            .then((detailedVehicle) => resolve(detailedVehicle))
            .catch((error) => {
              const errorContext: ErrorContext = {
                tags: {
                  message: `Unable to retrieve vehicle information for [regNr=${vehicle.regNumber}]`,
                  regNumber: vehicle.regNumber,
                },
                extras: { error },
              };
              Log.warn("registeredVehicles-info", errorContext);
              resolve(vehicle);
            });
        }
        return resolve(vehicle);
      });
    },
  );

  return Promise.all(processedVehicles);
}

export const useCars = (): {
  data: DetailedVehicle[] | null;
  isLoading: boolean;
  error: unknown;
} => {
  const { data, isFetching, error } = useUsersVehicles();

  const insuredVehicles = data?.filter(
    (vehicle) =>
      vehicle.warrantyStatus === WarrantyStatus.ACTIVE &&
      hasProperty(
        vehicle as unknown as Record<string, string | number | boolean>,
        "tfaCode",
      ),
  );

  // assumes that all license plate numbers that matches the pattern is a car
  const licensePlateRegex = new RegExp(ValidationType.DEFAULT_LICENSE_PLATE);
  const cars = insuredVehicles?.filter((vehicle): vehicle is DetailedVehicle =>
    licensePlateRegex.test(vehicle.regNumber),
  );

  return {
    data: cars ?? null,
    isLoading: isFetching,
    error,
  };
};
